import { useCallback, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { DeviceService } from '../../lib/deviceService/client';
import {
  deviceServiceBelowPrint, minPrintVersion, MIN_PRINT_VERSION_32, MIN_PRINT_VERSION_64,
} from '../../lib/deviceService/minimum';

type State =
  | { kind: 'busy' }
  | { kind: 'opened' }
  /** We KNOW the Device Service is older than the print endpoint on its own branch. */
  | { kind: 'upgrade'; version: string; bits: string | null }
  /** `detail` is the technical reason (a status code), shown under the prose for support to quote. */
  | { kind: 'error'; message: string; detail: string | null }
  | null;

/**
 * Printing an .eft from the Resources menu.
 *
 * The Device Service does all of it on the desktop — it opens its own file chooser and print dialog — so
 * there is no print UI here beyond saying what happened. The dialog matters mainly because the JavaFX
 * window can open BEHIND the browser: without a word on screen a successful print looks like a dead menu
 * item.
 *
 * The dialog comes back as a node for the caller to render, but takes itself to the document body: its
 * caller is the app bar, whose backdrop-filter makes it the containing block for `position: fixed`
 * descendants — an overlay left inside it would be laid out within the 66px header rather than over the
 * page, and would vanish with the nav sheet on a narrow screen.
 *
 * The version it learns is deliberately NOT fed to DeviceServiceStatusProvider: this lives in the app bar,
 * which is rendered outside that provider, and useDeviceServiceStatus throws there.
 */
export function usePrintEft(): { print: () => void; dialog: ReactNode } {
  const [state, setState] = useState<State>(null);
  /*
   * Which attempt owns the dialog. Connecting can sit for up to 30s on a local-network permission prompt,
   * and that wait must stay dismissible — without this, a run the user closed would pop its result back up.
   */
  const run = useRef(0);
  const close = useCallback(() => { run.current++; setState(null); }, []);

  const print = useCallback(async () => {
    const id = ++run.current;
    const settle = (s: State) => { if (run.current === id) setState(s); };
    setState({ kind: 'busy' });

    let svc: DeviceService;
    try {
      svc = await DeviceService.connect();
    } catch (e) {
      // Discovery's errors are already written for the customer — an unreachable service, a browser
      // permission, a mixed-content block — so they are the message rather than a detail line.
      settle({ kind: 'error', message: errorText(e), detail: null });
      return;
    }

    try {
      await svc.printEft();
      settle({ kind: 'opened' });
    } catch (e) {
      // A build without the route 404s here, and that failure says nothing about the printer. Ask the
      // service what it is before reporting anything else — BOTH what it is and which branch it is on,
      // since the two Device Service lines are numbered independently.
      const [version, bits] = await Promise.all([
        svc.version().then((v) => v.trim()).catch(() => null),
        svc.bits().then((b) => b.trim()).catch(() => null),
      ]);
      if (version && deviceServiceBelowPrint(version, bits)) {
        settle({ kind: 'upgrade', version, bits });
      } else {
        // An unreadable version is not evidence that it is too old, but on this call it is still the
        // likeliest explanation — so it is offered, not asserted.
        const note = version ? '' : ` This workstation did not report its version; printing needs ${requirement(bits)}.`;
        settle({ kind: 'error', message: 'The Device Service did not accept the print request.' + note, detail: errorText(e) });
      }
    }
  }, []);

  return { print: () => void print(), dialog: <PrintDialog state={state} onClose={close} /> };
}

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : 'The Device Service could not start a print job.';
}

/**
 * What this workstation needs, in one phrase.
 *
 * Both branches are named when the architecture is unknown rather than picking the likelier one: the two
 * lines are numbered independently, so a single number would be wrong advice for half of the people who
 * read it — and "update to 2.0.5" is unfollowable on a 32-bit install, where no such build exists.
 */
function requirement(bits: string | null): string {
  const min = minPrintVersion(bits);
  return min
    ? `Device Service v${min} or newer`
    : `Device Service v${MIN_PRINT_VERSION_64} or newer (64-bit), or v${MIN_PRINT_VERSION_32} or newer (32-bit)`;
}

function PrintDialog({ state, onClose }: { state: State; onClose: () => void }) {
  if (!state) return null;
  return createPortal(
    <div className="confirm-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="confirm-card" role="dialog" aria-modal="true" aria-label="Print an EFT">
        <h3 className="confirm-title">
          {state.kind === 'upgrade' ? 'Update the Device Service' : state.kind === 'error' ? 'Could not print' : 'Print an EFT'}
        </h3>
        <div className="confirm-body">
          {state.kind === 'busy' && <p>Opening the print window on this computer…</p>}
          {state.kind === 'opened' && (
            <p>
              The print window has opened <b>on this computer</b> — check behind your browser if you cannot
              see it. Choose the .eft file to print there.
            </p>
          )}
          {state.kind === 'upgrade' && (
            <p>
              Printing needs <b>{requirement(state.bits)}</b>. This workstation runs <b>v{state.version}</b>
              {state.bits ? ` (${state.bits}-bit)` : ''}, which has no print support — update it and try
              again.
            </p>
          )}
          {state.kind === 'error' && (
            <>
              <p>{state.message}</p>
              {state.detail && <p style={{ color: 'var(--text-dim)', fontSize: 13 }}>{state.detail}</p>}
            </>
          )}
        </div>
        <div className="confirm-actions">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          {state.kind === 'upgrade' && (
            <Link to="/app/downloads" className="btn btn-primary" onClick={onClose}>Go to downloads</Link>
          )}
          {state.kind === 'error' && (
            <Link to="/app/help/device" className="btn btn-primary" onClick={onClose}>Device status</Link>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
